import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Card, 
  CardContent, 
  Grid,
  Paper,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Login as LoginIcon, 
  PersonAdd as RegisterIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  LocalHospital as HospitalIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckIcon,
  Article as ArticleIcon,
  ContactSupport as SupportIcon,
  Info as InfoIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import landingTheme from '../../theme/landingTheme';
import LandingLayout from '../../components/LandingLayout';

const LandingPage = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const features = [
    { icon: <HospitalIcon sx={{ fontSize: 50, color: 'white', filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />, title: 'Gestione Residenti', description: 'Anagrafica completa con storico medico e terapie personalizzate' },
    { icon: <PeopleIcon sx={{ fontSize: 50, color: 'white', filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />, title: 'Coordinamento Staff', description: 'Gestione turni infermieri e personale sanitario' },
    { icon: <ScheduleIcon sx={{ fontSize: 50, color: 'white', filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />, title: 'Calendario Visite', description: 'Pianificazione appuntamenti medici e controlli periodici' },
    { icon: <MedicalIcon sx={{ fontSize: 50, color: 'white', filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />, title: 'Terapie Farmacologiche', description: 'Monitoraggio somministrazioni e scadenze farmaci' },
    { icon: <AnalyticsIcon sx={{ fontSize: 50, color: 'white', filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />, title: 'Report & Analytics', description: 'Dashboard con KPI e statistiche in tempo reale' },
    { icon: <SecurityIcon sx={{ fontSize: 50, color: 'white', filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />, title: 'Sicurezza GDPR', description: 'Dati criptati e conformità normativa sanitaria' }
  ];

  const pricingPlans = [
    {
      name: 'Basic',
      price: '49',
      period: 'mese',
      residents: 'Fino a 20 residenti',
      features: [
        'Gestione anagrafica residenti',
        'Calendario visite mediche',
        'Gestione terapie base',
        '2 utenti staff',
        'Supporto email',
        'Backup giornaliero'
      ],
      color: landingTheme.colors.primaryLight,
      popular: false
    },
    {
      name: 'Premium',
      price: '99',
      period: 'mese',
      residents: 'Fino a 50 residenti',
      features: [
        'Tutte le funzioni Basic',
        'Gestione completa staff',
        'Report avanzati',
        '5 utenti staff',
        'Supporto prioritario',
        'Integrazione sensori IoT',
        'App mobile'
      ],
      color: landingTheme.colors.primary,
      popular: true
    },
    {
      name: 'Premium Plus',
      price: '199',
      period: 'mese',
      residents: 'Residenti illimitati',
      features: [
        'Tutte le funzioni Premium',
        'Multi-struttura',
        'Utenti illimitati',
        'Supporto telefonico 24/7',
        'Formazione personalizzata',
        'API personalizzate',
        'Account manager dedicato'
      ],
      color: landingTheme.colors.primaryDark,
      popular: false
    }
  ];

  return (
    <LandingLayout>
      <Box sx={{ minHeight: '100vh', background: landingTheme.gradients.primary, fontFamily: 'Montserrat, sans-serif' }}>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 6 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ color: 'white' }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  mb: 3
                }}
              >
                Gestionale Anziani
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 400,
                  fontSize: { xs: '1.3rem', md: '1.6rem' },
                  textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.5
                }}
              >
                La piattaforma SaaS completa per la gestione di case di riposo e strutture per anziani
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: '1.1rem',
                  mb: 4,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  lineHeight: 1.7,
                  fontWeight: 300
                }}
              >
                Semplifica la gestione quotidiana con strumenti digitali intuitivi. 
                Monitora residenti, coordina il personale e ottimizza i processi assistenziali.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RegisterIcon />}
                  onClick={() => navigate('/register')}
                  sx={{
                    bgcolor: '#FF6F00',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#E65100',
                      transform: 'translateY(-2px)',
                      boxShadow: 6
                    },
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700
                  }}
                >
                  Prova Gratis 30 Giorni
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'white',
                    borderWidth: 2,
                    color: 'white',
                    '&:hover': {
                      borderWidth: 2,
                      bgcolor: 'rgba(255,255,255,0.15)'
                    },
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700
                  }}
                >
                  Accedi
                </Button>
              </Stack>

              <Stack direction="row" spacing={3} sx={{ color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon sx={{ color: '#4caf50' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Nessuna carta richiesta</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon sx={{ color: '#4caf50' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Supporto in italiano</Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', p: 4, border: '1px solid rgba(255,255,255,0.3)' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'white', textAlign: 'center', mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                🎯 Inizia Subito con il Trial Gratuito
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>30</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Giorni Gratis</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>3</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Residenti Inclusi</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, textAlign: 'center' }}>
                  ✓ Accesso completo a tutte le funzionalità<br />
                  ✓ Nessun vincolo contrattuale<br />
                  ✓ Disdici quando vuoi
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Box id="features" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            Funzionalità Complete
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.9)', fontWeight: 400, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            Tutto ciò che serve per gestire la tua struttura in modo efficiente
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  elevation={3}
                  sx={{ 
                    height: '100%',
                    p: 3,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                      bgcolor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'white', textAlign: 'center', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', lineHeight: 1.6, fontWeight: 500 }}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ background: landingTheme.gradients.primary, py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            Piani e Prezzi
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, color: 'white', fontWeight: 400, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            Scegli il piano più adatto alle tue esigenze
          </Typography>
          
          <Grid container spacing={4} alignItems="stretch">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  elevation={plan.popular ? 12 : 4}
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(10px)',
                    border: plan.popular ? '2px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.3)',
                    transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: plan.popular ? 'scale(1.08)' : 'scale(1.03)',
                      bgcolor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  {plan.popular && (
                    <Chip 
                      label="PIÙ POPOLARE" 
                      sx={{ 
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: '#ff9800',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.75rem'
                      }} 
                    />
                  )}
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', textAlign: 'center', mb: 2, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                      {plan.name}
                    </Typography>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', display: 'inline', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                        €{plan.price}
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', display: 'inline' }}>
                        /{plan.period}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ textAlign: 'center', mb: 3, color: 'white', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.2)', p: 1, borderRadius: 1, border: '1px solid rgba(255,255,255,0.3)' }}>
                      {plan.residents}
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.3)' }} />
                    <Stack spacing={1.5} sx={{ mb: 4 }}>
                      {plan.features.map((feature, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <CheckIcon sx={{ color: '#4caf50', fontSize: 20, mt: 0.3, filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))' }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>{feature}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Button 
                      variant={plan.popular ? "contained" : "outlined"}
                      fullWidth
                      size="large"
                      onClick={() => navigate('/register')}
                      sx={{ 
                        py: 1.5,
                        fontWeight: 700,
                        borderColor: 'white',
                        color: plan.popular ? 'landingTheme.colors.primary' : 'white',
                        bgcolor: plan.popular ? 'white' : 'transparent',
                        '&:hover': { 
                          bgcolor: plan.popular ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.1)',
                          borderColor: 'white'
                        }
                      }}
                    >
                      Inizia Ora
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="h6" sx={{ color: 'white', mb: 2, textShadow: '1px 1px 2px rgba(0,0,0,0.3)', fontWeight: 600 }}>
              Hai bisogno di un piano personalizzato?
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => scrollToSection('contact')}
              sx={{ 
                bgcolor: 'white',
                color: landingTheme.colors.primary,
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Contatta il Reparto Vendite
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Target Section */}
      <Box id="target" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            Dedicato a
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.9)', fontWeight: 400, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            La soluzione ideale per diverse tipologie di strutture
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)' }}>
                <HospitalIcon sx={{ fontSize: 60, color: 'white', mb: 2, filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                  Case di Riposo
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, fontWeight: 500 }}>
                  Gestisci in modo efficiente tutti gli aspetti della struttura: 
                  anagrafica residenti, terapie, visite mediche, coordinamento staff infermieristico 
                  e comunicazione con i familiari.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)' }}>
                <BusinessIcon sx={{ fontSize: 60, color: 'white', mb: 2, filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                  RSA e Strutture Sanitarie
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, fontWeight: 500 }}>
                  Strumenti avanzati per la gestione sanitaria: cartelle cliniche digitali,
                  monitoraggio parametri vitali, integrazione con sensori IoT e report 
                  conformi alle normative sanitarie.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)' }}>
                <HomeIcon sx={{ fontSize: 60, color: 'white', mb: 2, filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                  Comunità Alloggio
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, fontWeight: 500 }}>
                  Soluzioni scalabili per comunità di piccole e medie dimensioni.
                  Gestione semplificata ma completa, con focus sull'assistenza personalizzata
                  e il coinvolgimento attivo dei residenti.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)' }}>
                <PeopleIcon sx={{ fontSize: 60, color: 'white', mb: 2, filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                  Gruppi Multi-Struttura
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, fontWeight: 500 }}>
                  Gestisci più strutture da un'unica dashboard centralizzata.
                  Database isolati per sicurezza, con possibilità di analisi aggregate
                  e benchmarking tra le diverse sedi.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box id="contact" sx={{ background: landingTheme.gradients.primary, py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            Contattaci
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, color: 'white', fontWeight: 400, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            Il nostro team è a tua disposizione per qualsiasi informazione
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, textAlign: 'center', height: '100%', bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' }}>
                <PhoneIcon sx={{ fontSize: 60, color: landingTheme.colors.primary, mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  Telefono
                </Typography>
                <Typography variant="body1" sx={{ color: '#424242', mb: 2, fontWeight: 500 }}>
                  Contatta il nostro team per informazioni, demo e supporto
                </Typography>
                <Typography variant="h6" sx={{ color: landingTheme.colors.primary, fontWeight: 700 }}>
                  (+39) 379 248 9784
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242', mt: 1, fontWeight: 500 }}>
                  Lun-Ven: 9:00-13:00<br />Lun-Ven: 15:00-19:00
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, textAlign: 'center', height: '100%', bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' }}>
                <EmailIcon sx={{ fontSize: 60, color: landingTheme.colors.primary, mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ color: '#424242', mb: 2, fontWeight: 500 }}>
                  Inviaci una email per qualsiasi richiesta. Risposta entro 24h lavorative
                </Typography>
                <Typography variant="body1" sx={{ color: landingTheme.colors.primary, fontWeight: 700 }}>
                  info@reaktice.com
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242', mt: 2, fontWeight: 500 }}>
                  Vendite: info@reaktice.com<br />
                  Supporto: info@reaktice.com
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 4, textAlign: 'center', height: '100%', bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' }}>
                <BusinessIcon sx={{ fontSize: 60, color: landingTheme.colors.primary, mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  Sede
                </Typography>
                <Typography variant="body1" sx={{ color: '#424242', mb: 2, fontWeight: 500 }}>
                  Reaktice S.r.l.s.<br />
                  Firenze, Italy
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242', mt: 1, fontWeight: 600 }}>
                  P.IVA: 07249190484
                </Typography>
                <Typography variant="body2" sx={{ color: landingTheme.colors.primary, mt: 2, fontWeight: 600 }}>
                  Innovazione Tecnologica<br />
                  IoT • Software • AI
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      </Box>
    </LandingLayout>
  );
};

export default LandingPage;
